import { useAuth } from "contexts/auth";
import DashBoardAdmin from "pages/authentication/dashboard/dashboard-admin";
import { useEffect } from "react";
import { Page } from "v2/components";
import { ProductivityTabBuyer, ProductivityTabSeller } from "./ProductivityTab";
import { OperationalDashboardTab } from "./OperationalDashboardTab";
import { PainelGovernanca } from "modules/governance/pages/PainelGovernanca";
import { isGovernanceEnabled } from "modules/governance/feature-flag";
import { useQuery } from "@tanstack/react-query";
import { HojeTab, getRhHoje, mostrarHoje } from "./HojeTab";
import { AtivosTab, ComprasTab, abaVisivel, seloDaAba, getRhAbas, getRhPrevisto, getRhGente, getRhVisibilidade, mostrarAbas, mostrarAbaDaFolha } from "./AbasRh";
import { PrevistoTab } from "./AbasRh/PrevistoTab";
import { GenteTab } from "./AbasRh/GenteTab";

export function Dashboard() {
  const { user, isExternalAuth } = useAuth();
  const userType = user?.isAdmin ? 'ADMIN' : user?.isSeller ? 'SELLER' : 'BUYER';
  const { VITE_APP_AMBIENT } = import.meta.env;
  const isNotProd = VITE_APP_AMBIENT !== 'prod' && VITE_APP_AMBIENT !== 'production';

  // Hoje é a primeira aba do comprador quando há o que mostrar (spec do RH §6, E3).
  // ponytail: a aba entra depois da resposta; quem trocar de aba no primeiro segundo
  // pode ver o índice deslocar. Se incomodar, guardar a aba por nome no PageRoot.
  const hoje = useQuery({
    queryKey: ['rh-hoje', user?.organization?.id],
    queryFn: getRhHoje,
    enabled: userType === 'BUYER',
    retry: 1,
  });
  // Ativos e Compras (escopo 2.3): uma resposta para as duas; o backend liga por empresa.
  const abas = useQuery({
    queryKey: ['rh-abas', user?.organization?.id],
    queryFn: getRhAbas,
    enabled: userType === 'BUYER',
    retry: 1,
  });
  // Selos e a aba "O que vem aí" (contrato §5): só ela decide por `visivel`.
  const visibilidade = useQuery({
    queryKey: ['rh-visibilidade', user?.organization?.id],
    queryFn: getRhVisibilidade,
    enabled: userType === 'BUYER',
    retry: 1,
  });
  const previsto = useQuery({
    queryKey: ['rh-previsto', user?.organization?.id],
    queryFn: getRhPrevisto,
    enabled: userType === 'BUYER' && abaVisivel(visibilidade.data, 'previsto'),
    retry: 1,
  });
  const gente = useQuery({
    queryKey: ['rh-gente', user?.organization?.id],
    queryFn: getRhGente,
    enabled: userType === 'BUYER' && abaVisivel(visibilidade.data, 'gente'),
    retry: 1,
  });

  useEffect(() => {
    if (user && isExternalAuth && user.emailIsConfirmed && user?.organization?.hasAgreedToTermsAndConditions && user.hasPermissionRequest) {
      window.close();
    }
  }, [user, isExternalAuth]);

  return (
    <>
      {userType === 'ADMIN' && (
        <Page title="Dashboard">
          <Page.Tab name="Admin">
            <DashBoardAdmin />
          </Page.Tab>
        </Page>
      )}
      {userType === 'BUYER' && (
        <Page title="Dashboard">
          {mostrarHoje(hoje) && (
            <Page.Tab name="Hoje">
              <HojeTab dados={hoje.data} erro={hoje.isError} selo={seloDaAba(visibilidade.data, 'hoje')} />
            </Page.Tab>
          )}
          {mostrarAbas(abas) && (
            <Page.Tab name="Ativos e funcionários">
              <AtivosTab dados={abas.data} erro={abas.isError} selo={seloDaAba(visibilidade.data, 'ativos')} />
            </Page.Tab>
          )}
          {mostrarAbas(abas) && (
            <Page.Tab name="Compras e movimentações">
              <ComprasTab dados={abas.data} erro={abas.isError} selo={seloDaAba(visibilidade.data, 'compras')} />
            </Page.Tab>
          )}
          {mostrarAbaDaFolha("previsto", visibilidade, previsto) && (
            <Page.Tab name="O que vem aí">
              <PrevistoTab dados={previsto.data} erro={previsto.isError} visibilidade={visibilidade.data!} />
            </Page.Tab>
          )}
          {mostrarAbaDaFolha("gente", visibilidade, gente) && (
            <Page.Tab name="Minha gente">
              <GenteTab dados={gente.data} erro={gente.isError} visibilidade={visibilidade.data!} />
            </Page.Tab>
          )}
          {isNotProd && (
            <Page.Tab name="Painel Operacional">
              <OperationalDashboardTab />
            </Page.Tab>
          )}
          <Page.Tab name="Entradas e saídas">
            <ProductivityTabBuyer />
          </Page.Tab>
          {/* Só o piloto vê a aba; os demais viam "em implantação" (rollout 09/10). */}
          {isGovernanceEnabled(user) && (
            <Page.Tab name="Visão Executiva de Governança">
              <PainelGovernanca />
            </Page.Tab>
          )}
        </Page>
      )}
      {userType === 'SELLER' && (
        <Page title="Dashboard">
          <Page.Tab name="Entradas e saídas">
            <ProductivityTabSeller />
          </Page.Tab>
        </Page>
      )}
    </>
  );
}
